import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type CommissionSeed } from '../components/home/CommissionVariants';
import { trackEvent } from '../lib/posthog';

/**
 * CommissionRequestContext — one app-wide entry point for "request an outfit
 * made".
 *
 * Every call site (Home CTA, floating chat bar, outfit post, designer profile,
 * empty search state, …) uses `useCommissionRequest().open(...)`. Since the
 * full-screen Order page replaced the inline chat modal, calling `open()` now
 * navigates the buyer to `/order`. Any seed context (inspiration image,
 * designer name/slug for a direct booking) is passed via location state so
 * the Order page can pre-populate the brief without another round-trip.
 */

interface OpenOptions extends CommissionSeed {
  /** Where the request was launched from — for analytics only. */
  source?: string;
}

interface CommissionRequestContextType {
  open: (opts?: OpenOptions) => void;
  /** Kept for callers still using the old shape — no-op on the routed flow. */
  updateSeed: (partial: Partial<CommissionSeed>) => void;
  /** Kept for callers still using the old shape — no-op on the routed flow. */
  close: () => void;
  /** Retained for legacy call sites; always false in the routed flow. */
  isOpen: boolean;
}

const CommissionRequestContext = createContext<CommissionRequestContextType | undefined>(undefined);

export const CommissionRequestProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const open = useCallback((opts?: OpenOptions) => {
    const { source, ...seedFields } = opts ?? {};
    const hasSeed = !!(seedFields.inspirationImage || seedFields.designerName || seedFields.designerSlug);

    try {
      trackEvent('commission_request_opened', {
        source: source || 'unknown',
        has_inspiration: !!seedFields.inspirationImage,
        designer_name: seedFields.designerName || null,
        designer_slug: seedFields.designerSlug || null,
        is_direct_booking: !!seedFields.designerSlug,
      });
    } catch {
      /* analytics must never block the UI */
    }

    // Always record where the brief was opened from, so its back/exit returns
    // the buyer to that page rather than guessing from history.
    const from = `${location.pathname}${location.search}`;
    navigate('/order', { state: { ...(hasSeed ? { seed: seedFields } : {}), source, from } });
  }, [navigate, location.pathname, location.search]);

  const updateSeed = useCallback(() => { /* no-op — the Order page owns state now */ }, []);
  const close = useCallback(() => { /* no-op — Order page has its own back/exit */ }, []);

  return (
    <CommissionRequestContext.Provider value={{ open, updateSeed, close, isOpen: false }}>
      {children}
    </CommissionRequestContext.Provider>
  );
};

export const useCommissionRequest = () => {
  const ctx = useContext(CommissionRequestContext);
  if (!ctx) {
    throw new Error('useCommissionRequest must be used within a CommissionRequestProvider');
  }
  return ctx;
};
